import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  TextField,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SECTIONS = ["A", "B", "C", "D"];

const parseYearStart = (value) => {
  const startYear = Number(String(value || "").split("-")[0]);
  return Number.isFinite(startYear) ? startYear : null;
};

const getBatchYearOptions = (batchName) => {
  const [startYear, endYear] = String(batchName || "")
    .split("-")
    .map((value) => Number(value));
  const yearCount =
    Number.isFinite(startYear) && Number.isFinite(endYear) && endYear > startYear
      ? endYear - startYear
      : 4;

  return Array.from({ length: yearCount }, (_, index) => index + 1);
};

const getSemesterOptions = (yearNumber) => {
  const year = Number(yearNumber);
  return Number.isFinite(year) && year > 0 ? [year * 2 - 1, year * 2] : [];
};

export default function Attendance() {
  // Update attendanceData for FN/AN fields
  const handleAttendanceChange = (rowId, field, value) => {
    setAttendanceData((prev) =>
      prev.map((row) =>
        row.id === rowId ? { ...row, [field]: value.toUpperCase().replace(/[^AP]/gi, "") } : row
      )
    );
  };

  const [programs, setPrograms] = useState([]);
  const [branches, setBranches] = useState([]);
  const [batches, setBatches] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedYearNumber, setSelectedYearNumber] = useState("");
  const [selectedSemesterNumber, setSelectedSemesterNumber] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [fetchingStudents, setFetchingStudents] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [studentListMessage, setStudentListMessage] = useState("");
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  // Get user info from session (adjust as needed)
  const user = JSON.parse(sessionStorage.getItem("user") || "null");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const showError = (message) => {
    setSnackbarMessage(message);
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  };

  const selectedBatchDetails = useMemo(
    () => batches.find((batch) => String(batch.admittedbatch_id) === String(selectedBatch)),
    [batches, selectedBatch]
  );
  const activeAcademicYear = useMemo(
    () => academicYears.find((year) => Number(year.is_active) === 1) || academicYears[0],
    [academicYears]
  );
  const yearOptions = useMemo(
    () => (selectedBatchDetails ? getBatchYearOptions(selectedBatchDetails.admittedbatch) : []),
    [selectedBatchDetails]
  );
  const semesterOptions = useMemo(
    () => getSemesterOptions(selectedYearNumber),
    [selectedYearNumber]
  );

  useEffect(() => {
    if (clientId && collegeId) {
      setLoading(true);
      Promise.all([
        axios.get(`${API_BASE_URL}/api/attendance/programs/${clientId}/${collegeId}`),
        axios.get(`${API_BASE_URL}/api/acyear/by-college/${clientId}/${collegeId}`),
      ])
        .then(([programRes, academicYearRes]) => {
          setPrograms(programRes.data.data || []);
          setAcademicYears(academicYearRes.data.data || []);
        })
        .catch(() => {
          setPrograms([]);
          setAcademicYears([]);
          showError(
            "Unable to load programs or academic years. Please check the server connection."
          );
        })
        .finally(() => setLoading(false));
    }
  }, [clientId, collegeId]);

  useEffect(() => {
    if (selectedProgram && clientId && collegeId) {
      setLoading(true);
      axios
        .get(`${API_BASE_URL}/api/attendance/branches/${clientId}/${collegeId}/${selectedProgram}`)
        .then((res) => setBranches(res.data.data || []))
        .catch(() => {
          setBranches([]);
          showError("Unable to load branches. Please check the server connection.");
        })
        .finally(() => setLoading(false));
      setSelectedBranch("");
      setBatches([]);
      setSelectedBatch("");
      setSelectedYearNumber("");
      setSelectedSemesterNumber("");
      setSelectedSection("");
      setStudents([]);
      setAttendanceData([]);
    }
  }, [selectedProgram, clientId, collegeId]);

  useEffect(() => {
    if (selectedProgram && selectedBranch && clientId && collegeId) {
      setLoading(true);
      axios
        .get(
          `${API_BASE_URL}/api/attendance/batches/${clientId}/${collegeId}/${selectedProgram}/${selectedBranch}`
        )
        .then((res) => setBatches(res.data.data || []))
        .catch(() => {
          setBatches([]);
          showError("Unable to load batches. Please check the server connection.");
        })
        .finally(() => setLoading(false));
      setSelectedBatch("");
      setSelectedYearNumber("");
      setSelectedSemesterNumber("");
      setSelectedSection("");
      setStudents([]);
      setAttendanceData([]);
    }
  }, [selectedBranch, selectedProgram, clientId, collegeId]);

  // Reset section and students when batch changes
  useEffect(() => {
    setSelectedSection("");
    setStudents([]);
    setAttendanceData([]);

    if (!selectedBatchDetails) {
      setSelectedYearNumber("");
      setSelectedSemesterNumber("");
      return;
    }

    const batchStartYear = parseYearStart(selectedBatchDetails.admittedbatch);
    const activeStartYear = parseYearStart(activeAcademicYear?.academic_year);
    const derivedYear =
      batchStartYear && activeStartYear ? activeStartYear - batchStartYear + 1 : 1;
    const normalizedYear = yearOptions.includes(derivedYear) ? derivedYear : yearOptions[0];

    setSelectedYearNumber(normalizedYear ? String(normalizedYear) : "");
    setSelectedSemesterNumber(normalizedYear ? String(normalizedYear * 2 - 1) : "");
  }, [selectedBatch, selectedBatchDetails, activeAcademicYear, yearOptions]);

  useEffect(() => {
    const validSemesters = semesterOptions.map(String);
    if (!validSemesters.length) {
      setSelectedSemesterNumber("");
      return;
    }

    if (!validSemesters.includes(String(selectedSemesterNumber))) {
      setSelectedSemesterNumber(validSemesters[0]);
    }
  }, [semesterOptions, selectedSemesterNumber]);

  useEffect(() => {
    setStudents([]);
    setAttendanceData([]);
    setStudentListMessage("");
  }, [selectedYearNumber, selectedSemesterNumber]);

  // Handler for Get List button
  const handleGetList = async () => {
    if (
      !selectedProgram ||
      !selectedBranch ||
      !selectedBatch ||
      !selectedYearNumber ||
      !selectedSemesterNumber ||
      !selectedDate
    )
      return;
    setFetchingStudents(true);
    setStudentListMessage("");
    try {
      const params = {
        program_id: selectedProgram,
        branch_id: selectedBranch,
        admittedbatch_id: selectedBatch,
        year_number: selectedYearNumber,
        semester_number: selectedSemesterNumber,
        section: selectedSection || undefined,
        attendance_date: selectedDate,
        client_id: clientId,
        college_id: collegeId,
      };
      const res = await axios.get(`${API_BASE_URL}/api/attendance/students`, { params });
      const studentList = res.data.data || [];
      setStudents(studentList);
      // Map studentList to attendanceData for DataGrid
      setAttendanceData(
        studentList.map((stu, idx) => ({
          id: stu.student_id || idx + 1, // DataGrid needs unique id
          serial_no: idx + 1,
          student_rollno: stu.student_rollno,
          student_name: stu.student_name,
          student_status: stu.student_status,
          fn: stu.fn || "",
          an: stu.an || "",
          remarks: stu.remarks || "",
          fnPosted: stu.fnPosted,
          anPosted: stu.anPosted,
        }))
      );
      if (studentList.length === 0) {
        setStudentListMessage(
          "No students found for the selected Program, Branch, Batch, and Section."
        );
        setSnackbarMessage("No students found for the selected input.");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
      }
      // If FN is posted for all, show message to post AN
      if (studentList.length > 0 && studentList.every((stu) => stu.fnPosted && !stu.anPosted)) {
        setSnackbarMessage("Morning attendance posted, please post the afternoon attendance.");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
      }
    } catch (err) {
      setAttendanceData([]);
      setStudentListMessage("");
      showError(err.response?.data?.message || "Failed to fetch student list.");
    } finally {
      setFetchingStudents(false);
    }
  };

  // Save attendance handler
  const handleSaveAttendance = async (e) => {
    e.preventDefault();
    if (
      !selectedProgram ||
      !selectedBranch ||
      !selectedBatch ||
      !selectedYearNumber ||
      !selectedSemesterNumber ||
      !selectedDate
    ) {
      setSnackbarMessage("Please select all fields before saving.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    if (!attendanceData.length) {
      setSnackbarMessage("No student list to post attendance. Please click 'Get List' first.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    // Only post attendance for ACTIVE students, warn if any non-ACTIVE present
    const nonActive = attendanceData.filter((row) => row.student_status !== "ACTIVE");
    const activeRows = attendanceData.filter((row) => row.student_status === "ACTIVE");
    if (activeRows.length === 0) {
      setSnackbarMessage("No ACTIVE students to post attendance.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    // No warning for non-ACTIVE students
    setSavingAttendance(true);
    try {
      // Prepare students array for API (only ACTIVE)
      const studentsToSave = activeRows.map((row) => ({
        student_rollno: row.student_rollno,
        fn: row.fn && row.fn.trim().toUpperCase() === "A" ? "A" : "P", // Default to 'P' if blank
        an:
          row.fnPosted && (row.an === undefined || row.an === null || row.an.trim() === "")
            ? "P"
            : row.an && row.an.trim().toUpperCase() === "A"
            ? "A"
            : row.an && row.an.trim().toUpperCase() === "P"
            ? "P"
            : null,
        remarks: row.remarks || null,
      }));
      const payload = {
        client_id: clientId,
        program_id: selectedProgram,
        branch_id: selectedBranch,
        admittedbatch_id: selectedBatch,
        year_number: selectedYearNumber,
        semester_number: selectedSemesterNumber,
        section: selectedSection || null,
        attendance_date: selectedDate,
        students: studentsToSave,
        college_id: collegeId,
        created_by: user?.user_name || user?.user_id || "",
        modified_by: user?.user_name || user?.user_id || "",
      };
      await axios.post(`${API_BASE_URL}/api/attendance/save`, payload);
      // Determine if FN or AN is being posted
      let postedType = "FN";
      if (
        activeRows.length > 0 &&
        activeRows.every((row) => row.fnPosted && row.an && !row.anPosted)
      ) {
        postedType = "AN";
      } else if (activeRows.length > 0 && activeRows.every((row) => row.fnPosted)) {
        postedType = "AN";
      }
      const formattedDate = selectedDate ? new Date(selectedDate).toLocaleDateString("en-GB") : "";

      setSnackbarMessage(`${formattedDate} - ${postedType} attendance posted successfully!`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      showError(err.response?.data?.message || "Failed to post attendance.");
    } finally {
      setSavingAttendance(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 2 }}>
        <Toolbar>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            ATTENDANCE
          </Typography>
        </Toolbar>

        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            label="Program"
            name="program_id"
            select
            value={selectedProgram}
            onChange={(e) => setSelectedProgram(e.target.value)}
            size="medium"
            sx={{ minWidth: 180 }}
            disabled={loading || !programs.length}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true, displayEmpty: true }}
          >
            <option value="">Select Program</option>
            {programs.map((p) => (
              <option key={p.program_id} value={p.program_id}>
                {p.program_name}
              </option>
            ))}
          </TextField>

          <TextField
            label="Branch"
            name="branch_id"
            select
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.target.value)}
            size="medium"
            sx={{ minWidth: 180 }}
            disabled={loading || !branches.length || !selectedProgram}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true, displayEmpty: true }}
          >
            <option value="">Select Branch</option>
            {branches.map((b) => (
              <option key={b.branch_id} value={b.branch_id}>
                {b.branch_name}
              </option>
            ))}
          </TextField>

          <TextField
            label="Batch"
            name="batch_id"
            select
            value={selectedBatch}
            onChange={(e) => setSelectedBatch(e.target.value)}
            size="medium"
            sx={{ minWidth: 180 }}
            disabled={loading || !batches.length || !selectedBranch}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true, displayEmpty: true }}
          >
            <option value="">Select Batch</option>
            {batches.map((b) => (
              <option key={b.admittedbatch_id} value={b.admittedbatch_id}>
                {b.admittedbatch}
              </option>
            ))}
          </TextField>

          <TextField
            label="Year"
            name="year_number"
            select
            value={selectedYearNumber}
            onChange={(e) => setSelectedYearNumber(e.target.value)}
            size="medium"
            sx={{ minWidth: 110 }}
            disabled={!selectedBatch || !yearOptions.length}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true, displayEmpty: true }}
          >
            <option value="">Select</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </TextField>

          <TextField
            label="Semester"
            name="semester_number"
            select
            value={selectedSemesterNumber}
            onChange={(e) => setSelectedSemesterNumber(e.target.value)}
            size="medium"
            sx={{ minWidth: 130 }}
            disabled={!selectedYearNumber || !semesterOptions.length}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true, displayEmpty: true }}
          >
            <option value="">Select</option>
            {semesterOptions.map((semester) => (
              <option key={semester} value={semester}>
                {semester}
              </option>
            ))}
          </TextField>

          <TextField
            label="Section"
            name="section"
            select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            size="medium"
            sx={{ minWidth: 100 }}
            disabled={!selectedBatch}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: true, displayEmpty: true }}
          >
            <option value="">Select</option>
            {SECTIONS.map((sec) => (
              <option key={sec} value={sec}>
                {sec}
              </option>
            ))}
          </TextField>

          <TextField
            label="Date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            size="medium"
            sx={{ minWidth: 160 }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: new Date().toISOString().split("T")[0] }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleGetList}
            disabled={
              !selectedProgram ||
              !selectedBranch ||
              !selectedBatch ||
              !selectedYearNumber ||
              !selectedSemesterNumber ||
              !selectedDate ||
              fetchingStudents
            }
            sx={{ color: "#fff" }}
          >
            {fetchingStudents ? <CircularProgress size={22} /> : "Get List"}
          </Button>
        </Box>

        {attendanceData.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Post Attendance (Post Absents : Type A for Absent Only)
            </Typography>
            <Box component="form" onSubmit={handleSaveAttendance}>
              <DataGrid
                rows={attendanceData}
                columns={[
                  { field: "serial_no", headerName: "S.No", width: 60 },
                  { field: "student_rollno", headerName: "Roll No", width: 140 },
                  {
                    field: "fn",
                    headerName: "FN (A/P)",
                    width: 120,
                    renderCell: (params) => (
                      <TextField
                        value={params.value}
                        onChange={(e) =>
                          handleAttendanceChange(params.row.id, "fn", e.target.value)
                        }
                        inputProps={{
                          maxLength: 1,
                          style: { textTransform: "uppercase", width: 40 },
                        }}
                        size="medium"
                        variant="outlined"
                        sx={{
                          width: 60,
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "#222", borderWidth: 2 },
                          },
                        }}
                        disabled={params.row.fnPosted || params.row.student_status !== "ACTIVE"}
                      />
                    ),
                  },
                  {
                    field: "an",
                    headerName: "AN (A/P)",
                    width: 120,
                    renderCell: (params) => (
                      <TextField
                        value={params.value}
                        onChange={(e) =>
                          handleAttendanceChange(params.row.id, "an", e.target.value)
                        }
                        inputProps={{
                          maxLength: 1,
                          style: { textTransform: "uppercase", width: 40 },
                        }}
                        size="medium"
                        variant="outlined"
                        sx={{
                          width: 60,
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "#222", borderWidth: 2 },
                          },
                        }}
                        disabled={
                          !params.row.fnPosted ||
                          params.row.anPosted ||
                          params.row.student_status !== "ACTIVE"
                        }
                      />
                    ),
                  },
                  { field: "student_name", headerName: "Student Name", width: 400 },
                  {
                    field: "student_status",
                    headerName: "Status",
                    width: 120,
                    renderCell: (params) =>
                      params.value !== "ACTIVE" ? (
                        <span style={{ color: "#d32f2f", fontWeight: 600 }}>{params.value}</span>
                      ) : (
                        <span style={{ color: "#388e3c" }}>ACTIVE</span>
                      ),
                  },
                ]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 20, 50, 100]}
                disableRowSelectionOnClick
                autoHeight
                sx={{ background: "#fff", borderRadius: 1, mt: 1 }}
                loading={fetchingStudents}
              />
              <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-start" }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={savingAttendance}
                  sx={{ color: "#fff" }}
                >
                  {savingAttendance ? <CircularProgress size={22} /> : "Save Attendance"}
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {!fetchingStudents && studentListMessage && attendanceData.length === 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {studentListMessage}
          </Alert>
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
            elevation={6}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
}
